(() => {
  'use strict';

  const config = window.TTSWishlistConfig || {};
  const proxyUrl = config.proxyUrl || '/apps/tts-wishlist';
  const wishlistUrl = config.wishlistUrl || '/pages/wishlist';
  const loginUrl = config.loginUrl || '/account/login';
  const toggleSelector = '.wishlist-productcard, .wishlist-productpage a';
  const savedProductIds = new Set();
  let knownWishlistItems = [];
  let lastWishlistLoadFailed = false;

  const wishlistRoot = () => document.querySelector('[data-tts-wishlist-page]');

  function productHandleFor(element) {
    if (element.dataset.productHandle) return element.dataset.productHandle;

    const card = element.closest('.product-card');
    const cardLink = card?.querySelector('a[href*="/products/"]');
    const productScope = element.closest('[data-product-url]');
    const urlValue = cardLink?.getAttribute('href') || productScope?.getAttribute('data-product-url');

    if (urlValue) {
      try {
        const path = new URL(urlValue, window.location.origin).pathname;
        const match = path.match(/\/products\/([^/?#]+)/);
        if (match) return decodeURIComponent(match[1]);
      } catch (_) {}
    }

    const currentMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return currentMatch ? decodeURIComponent(currentMatch[1]) : '';
  }

  function setButtonState(button, isSaved) {
    button.classList.toggle('tts-wishlisted', isSaved);
    button.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
    const title = isSaved ? 'Remove from wishlist' : 'Add to wishlist';
    button.setAttribute('aria-label', title);
    button.setAttribute('title', title);
  }

  function updateHeaderCount() {
    document.querySelectorAll('.wishlist-header').forEach((item) => {
      item.classList.remove('hidden');
      const link = item.querySelector('a');
      if (!link) return;
      link.href = wishlistUrl;
      link.setAttribute('aria-label', `Wishlist${savedProductIds.size ? ` (${savedProductIds.size} saved)` : ''}`);

      let count = link.querySelector('.tts-wishlist-count');
      if (!count) {
        count = document.createElement('span');
        count.className = 'tts-wishlist-count';
        link.appendChild(count);
      }
      count.textContent = String(savedProductIds.size);
      count.hidden = savedProductIds.size === 0;
    });
  }

  function initializeButtons() {
    document.querySelectorAll('.wishlist-productcard').forEach((button) => {
      button.classList.remove('hidden');
      button.setAttribute('type', 'button');
      if (!button.getAttribute('aria-pressed')) button.setAttribute('aria-pressed', 'false');
    });

    document.querySelectorAll('.wishlist-productpage').forEach((wrapper) => {
      wrapper.classList.remove('hidden');
      const link = wrapper.querySelector('a');
      if (link && !link.hasAttribute('aria-pressed')) link.setAttribute('aria-pressed', 'false');
    });

    document.querySelectorAll(toggleSelector).forEach((button) => {
      const id = button.dataset.productId;
      if (id) setButtonState(button, savedProductIds.has(String(id)));
    });

    updateHeaderCount();
  }

  async function apiRequest(method = 'GET', body = null) {
    const options = {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    };
    if (body !== null) {
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }

    const response = await fetch(proxyUrl, options);
    let result = {};
    try { result = await response.json(); } catch (_) {}

    if (!response.ok) {
      const error = new Error(result.error || `Wishlist request failed (${response.status})`);
      error.status = response.status;
      error.payload = result;
      throw error;
    }
    return result;
  }

  function showNotice(message, withLogin = false, target = document.body) {
    let notice = document.querySelector('.tts-wishlist-notice');
    if (!notice) {
      notice = document.createElement('div');
      notice.className = 'tts-wishlist-notice';
      notice.setAttribute('role', 'status');
      notice.setAttribute('aria-live', 'polite');
      document.body.appendChild(notice);
    }
    notice.replaceChildren(document.createTextNode(message));
    if (withLogin) {
      notice.appendChild(document.createTextNode(' '));
      const login = document.createElement('a');
      login.href = `${loginUrl}?return_url=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      login.textContent = 'Sign in';
      notice.appendChild(login);
      notice.appendChild(document.createTextNode(' to continue.'));
    }
    notice.classList.add('is-visible');
    window.clearTimeout(notice._ttsTimeout);
    notice._ttsTimeout = window.setTimeout(() => notice.classList.remove('is-visible'), 6500);
    if (target && target !== document.body) target.setAttribute('aria-busy', 'false');
  }

  function updateWishlistState(items) {
    knownWishlistItems = Array.isArray(items) ? items : [];
    savedProductIds.clear();
    knownWishlistItems.forEach((item) => savedProductIds.add(String(item.product_id)));
    initializeButtons();
  }

  function createElement(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  function money(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency,
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  async function getProduct(handle) {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('This product is unavailable.');
    return response.json();
  }

  async function refreshCartHeader() {
    try {
      const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('#cart-count').forEach((element) => {
        element.textContent = String(cart.item_count);
      });
      if (typeof window.Shopify?.formatMoney === 'function') {
        const formattedTotal = window.Shopify.formatMoney(
          cart.total_price,
          window.Shopify.money_format
        );
        document.querySelectorAll('#cart-total').forEach((element) => {
          element.textContent = formattedTotal;
        });
      }
    } catch (_) {
      // The cart add can still succeed even if the header refresh fails.
    }
  }

  async function addVariantToCart(variantId, button) {
    const numericVariantId = Number(variantId);
    if (!Number.isSafeInteger(numericVariantId) || numericVariantId <= 0) {
      showNotice('Please select an available product option before adding it to your cart.');
      return;
    }

    button.disabled = true;
    const originalText = button.textContent.trim() || 'Add to cart';
    button.textContent = 'Adding…';

    try {
      const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items: [{ id: numericVariantId, quantity: 1 }] }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.description || result.message || 'Could not add this item to the cart.');
      }

      // Refresh the visible cart count and, when available, Xtra's cart drawer.
      await refreshCartHeader();
      if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
        try { window.ajaxCart.load(false, false, false, true); } catch (_) {}
      }
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { variantId: numericVariantId, result },
      }));

      button.textContent = 'Added to cart';
      showNotice('Product added to your cart.');
      window.setTimeout(() => {
        if (button.isConnected) button.textContent = originalText;
      }, 2500);
    } catch (error) {
      button.textContent = originalText;
      showNotice(error.message || 'Could not add this item to the cart.');
    } finally {
      button.disabled = false;
    }
  }

  async function renderWishlistPage(items) {
    const root = wishlistRoot();
    if (!root) return;
    const status = root.querySelector('[data-tts-wishlist-status]');
    const list = root.querySelector('[data-tts-wishlist-items]');
    if (!list || !status) return;

    status.textContent = items.length ? 'Loading your saved products…' : 'Your wishlist is empty.';
    list.replaceChildren();
    if (!items.length) {
      status.textContent = 'Your wishlist is empty. Save products using the heart buttons while shopping.';
      return;
    }

    const grid = createElement('div', 'tts-wishlist-grid');
    let unavailableCount = 0;
    const productResults = await Promise.all(items.map(async (item) => {
      try {
        return { item, product: await getProduct(item.product_handle) };
      } catch (_) {
        unavailableCount += 1;
        return { item, product: null };
      }
    }));

    for (const { item, product } of productResults) {
      const card = createElement('article', 'tts-wishlist-card');
      card.dataset.productId = String(item.product_id);

      if (!product) {
        card.appendChild(createElement('p', 'tts-wishlist-unavailable', 'This saved product is no longer available.'));
        const removeUnavailable = createElement('button', 'tts-wishlist-remove', 'Remove');
        removeUnavailable.type = 'button';
        removeUnavailable.dataset.ttsRemove = '';
        removeUnavailable.dataset.productId = String(item.product_id);
        card.appendChild(removeUnavailable);
        grid.appendChild(card);
        continue;
      }

      const imageLink = createElement('a', 'tts-wishlist-image-link');
      imageLink.href = product.url || `/products/${item.product_handle}`;
      if (product.images && product.images[0]) {
        const img = createElement('img', 'tts-wishlist-image');
        img.src = product.images[0];
        img.alt = product.title || '';
        img.loading = 'lazy';
        imageLink.appendChild(img);
      }
      card.appendChild(imageLink);

      const info = createElement('div', 'tts-wishlist-info');
      const title = createElement('h2', 'tts-wishlist-title');
      const titleLink = createElement('a', '', product.title || 'Product');
      titleLink.href = product.url || `/products/${item.product_handle}`;
      title.appendChild(titleLink);
      info.appendChild(title);

      const price = createElement('p', 'tts-wishlist-price', money(product.price));
      info.appendChild(price);

      const variants = Array.isArray(product.variants) ? product.variants : [];
      const availableVariants = variants.filter((variant) => variant.available);
      let variantSelect = null;
      let selectedVariantId = '';

      if (variants.length > 1 || (variants[0] && variants[0].title !== 'Default Title')) {
        const label = createElement('label', 'tts-wishlist-option-label', 'Choose options');
        variantSelect = createElement('select', 'tts-wishlist-option');
        variantSelect.setAttribute('aria-label', `Choose options for ${product.title}`);
        const placeholder = createElement('option', '', 'Select an option');
        placeholder.value = '';
        placeholder.selected = true;
        variantSelect.appendChild(placeholder);
        variants.forEach((variant) => {
          const option = createElement('option', '', `${variant.title}${variant.available ? '' : ' — Sold out'}`);
          option.value = String(variant.id);
          option.disabled = !variant.available;
          option.dataset.price = String(variant.price || product.price || 0);
          variantSelect.appendChild(option);
        });
        variantSelect.addEventListener('change', () => {
          variantSelect.removeAttribute('aria-invalid');
          const option = variantSelect.options[variantSelect.selectedIndex];
          if (option?.dataset.price) price.textContent = money(option.dataset.price);
        });
        label.appendChild(variantSelect);
        info.appendChild(label);
      } else if (variants[0]) {
        selectedVariantId = String(variants[0].id);
      }

      const actions = createElement('div', 'tts-wishlist-actions');
      const addButton = createElement('button', 'tts-wishlist-add', 'Add to cart');
      addButton.type = 'button';
      addButton.dataset.ttsAddToCart = '';
      addButton.disabled = availableVariants.length === 0;
      addButton.addEventListener('click', () => {
        const chosen = variantSelect ? variantSelect.value : selectedVariantId;
        const variant = variants.find((entry) => String(entry.id) === String(chosen));
        if (!chosen) {
          if (variantSelect) {
            variantSelect.setAttribute('aria-invalid', 'true');
            variantSelect.focus();
          }
          showNotice(variantSelect
            ? 'Please select a product option before adding it to your cart.'
            : 'This product is currently unavailable.');
          return;
        }
        if (!variant || !variant.available) {
          if (variantSelect) {
            variantSelect.setAttribute('aria-invalid', 'true');
            variantSelect.focus();
          }
          showNotice('That option is unavailable. Please choose an available option.');
          return;
        }
        addVariantToCart(chosen, addButton);
      });

      const removeButton = createElement('button', 'tts-wishlist-remove', 'Remove');
      removeButton.type = 'button';
      removeButton.dataset.ttsRemove = '';
      removeButton.dataset.productId = String(item.product_id);

      actions.append(addButton, removeButton);
      info.appendChild(actions);
      card.appendChild(info);
      grid.appendChild(card);
    }

    list.replaceChildren(grid);
    status.textContent = unavailableCount
      ? `Your saved products are shown below. ${unavailableCount} product${unavailableCount === 1 ? ' is' : 's are'} unavailable.`
      : `${items.length} saved product${items.length === 1 ? '' : 's'}.`;
  }

  async function loadWishlist() {
    const root = wishlistRoot();
    if (!root && !document.querySelector(toggleSelector) && !document.querySelector('.wishlist-header')) return;

    try {
      const result = await apiRequest('GET');
      lastWishlistLoadFailed = false;
      updateWishlistState(result.items || []);
      if (root) await renderWishlistPage(knownWishlistItems);
    } catch (error) {
      lastWishlistLoadFailed = true;
      if (root) {
        const status = root.querySelector('[data-tts-wishlist-status]');
        const list = root.querySelector('[data-tts-wishlist-items]');
        if (list) list.replaceChildren();
        if (status) {
          status.replaceChildren(document.createTextNode(
            error.status === 401 ? 'Please sign in to view your wishlist.' : 'We could not load your wishlist. Please refresh and try again.'
          ));
          if (error.status === 401) {
            status.appendChild(document.createTextNode(' '));
            const link = createElement('a', '', 'Sign in');
            link.href = `${loginUrl}?return_url=${encodeURIComponent(window.location.pathname + window.location.search)}`;
            status.appendChild(link);
          }
        }
      }
    }
  }

  document.addEventListener('click', async (event) => {
    const removeButton = event.target.closest('[data-tts-remove]');
    if (removeButton) {
      event.preventDefault();
      event.stopPropagation();
      const productId = removeButton.dataset.productId;
      if (!productId) return;
      removeButton.disabled = true;
      try {
        await apiRequest('POST', { action: 'remove', product_id: productId });
        savedProductIds.delete(String(productId));
        knownWishlistItems = knownWishlistItems.filter((item) => String(item.product_id) !== String(productId));
        initializeButtons();
        await renderWishlistPage(knownWishlistItems);
      } catch (error) {
        if (error.status === 401) showNotice('Please sign in to manage your wishlist.', true);
        else showNotice(error.message || 'Could not remove this product.');
      } finally {
        if (removeButton.isConnected) removeButton.disabled = false;
      }
      return;
    }

    const toggle = event.target.closest(toggleSelector);
    if (!toggle) return;
    event.preventDefault();
    event.stopPropagation();

    const productId = toggle.dataset.productId;
    const productHandle = productHandleFor(toggle);
    if (!productId || !productHandle) {
      showNotice('We could not identify this product. Please refresh the page and try again.');
      return;
    }

    const wasSaved = savedProductIds.has(String(productId));
    toggle.disabled = true;
    try {
      await apiRequest('POST', {
        action: wasSaved ? 'remove' : 'add',
        product_id: String(productId),
        product_handle: productHandle,
      });

      if (wasSaved) {
        savedProductIds.delete(String(productId));
        knownWishlistItems = knownWishlistItems.filter((item) => String(item.product_id) !== String(productId));
      } else {
        savedProductIds.add(String(productId));
        knownWishlistItems = [
          { product_id: String(productId), product_handle: productHandle, created_at: new Date().toISOString() },
          ...knownWishlistItems.filter((item) => String(item.product_id) !== String(productId)),
        ];
      }
      initializeButtons();
      if (wishlistRoot()) await renderWishlistPage(knownWishlistItems);
      showNotice(wasSaved ? 'Removed from your wishlist.' : 'Saved to your wishlist.');
    } catch (error) {
      if (error.status === 401) showNotice('Please sign in to save products across devices.', true);
      else showNotice(error.message || 'Wishlist update failed. Please try again.');
    } finally {
      if (toggle.isConnected) toggle.disabled = false;
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeButtons();
      loadWishlist();
    }, { once: true });
  } else {
    initializeButtons();
    loadWishlist();
  }
})();
